import { useRef, useState } from "react";

const addAmounts = (a, b) => Math.round((a + b) * 100) / 100;
const subtractAmounts = (a, b) => Math.round((a - b) * 100) / 100;

const usePrefillPaymentRequest = ({ setValue, watch, handleCommsSelected, updateMessageAmount, amountRef }) => {
  const [selectedSalesOrderIds, setSelectedSalesOrderIds] = useState([]);
  // The selected orders, kept in the order they were added, so we can fall back
  // to the oldest remaining order's customer details when one is removed.
  const selectedOrders = useRef([]);
  // Remembers the customer details we auto-populated from a sales order, so that
  // when orders are removed we only overwrite/clear values that came from an
  // order (never a manually-entered value), regardless of removal order.
  const autoFilled = useRef({ customerName: null, email: null, phone: null });

  const handleRecentPaymentSelected = (request) => {
    setValue("customerName", request.customerName || "");
    setValue("reference", "");
    updateMessageAmount(0);
    setValue("amount", "");

    if (
      (!request.recipientPhoneNumber && !request.recipientEmail) ||
      (request.recipientPhoneNumber && request.recipientEmail)
    ) {
      handleCommsSelected("share");
    }

    if (request.recipientPhoneNumber && !request.recipientEmail) {
      setValue("phone", request.recipientPhoneNumber || "");
      handleCommsSelected("sms");
    }

    if (!request.recipientPhoneNumber && request.recipientEmail) {
      setValue("email", request.recipientEmail || "");
      handleCommsSelected("email");
    }

    setTimeout(() => {
      amountRef.current?.querySelector("input")?.focus();
    }, 0);
  };

  const handleSalesOrderSelected = (x) => {
    const currentReference = watch("reference") || "";
    const currentAmount = parseFloat(watch("amount")) || 0;

    const newReference = currentReference
      ? `${currentReference}, ${x.number}`
      : x.number;
    const newAmount = addAmounts(currentAmount, x.remainingAmount ?? x.grossAmount);

    setValue("reference", newReference);
    setValue("amount", newAmount);
    updateMessageAmount(newAmount);

    setSelectedSalesOrderIds((prev) => [...prev, x.id]);
    selectedOrders.current = [...selectedOrders.current, x];

    if (!watch("customerName")) {
      setValue("customerName", x.customerName);
      autoFilled.current.customerName = x.customerName;
    }
    if (!watch("email")) {
      setValue("email", x.email || "");
      autoFilled.current.email = x.email || "";
    }
    if (!watch("phone")) {
      setValue("phone", x.phone || "");
      autoFilled.current.phone = x.phone || "";
    }
  };

  const handleSalesOrderDeselected = (x) => {
    const currentReference = watch("reference") || "";
    const currentAmount = parseFloat(watch("amount")) || 0;

    const newReference = currentReference
      .split(", ")
      .filter((ref) => ref.trim() !== x.number)
      .join(", ");
    const newAmount = Math.max(
      0,
      subtractAmounts(currentAmount, x.remainingAmount ?? x.grossAmount),
    );

    setValue("reference", newReference);
    setValue("amount", newAmount);
    updateMessageAmount(newAmount);

    setSelectedSalesOrderIds((prev) => prev.filter((id) => id !== x.id));

    const remainingOrders = selectedOrders.current.filter((o) => o.id !== x.id);
    selectedOrders.current = remainingOrders;

    // Re-point the auto-populated customer details at the oldest remaining order
    // (or clear them if none remain). Only touch a field that still holds the
    // value we auto-filled, so a manually-entered value is never overwritten.
    const oldest = remainingOrders[0];
    const repoint = (field, orderValue) => {
      if (watch(field) !== autoFilled.current[field]) return; // manually changed
      const value = oldest ? orderValue(oldest) : "";
      setValue(field, value);
      autoFilled.current[field] = oldest ? value : null;
    };

    repoint("customerName", (o) => o.customerName);
    repoint("email", (o) => o.email || "");
    repoint("phone", (o) => o.phone || "");
  };

  const resetSalesOrderSelection = () => {
    setSelectedSalesOrderIds([]);
    selectedOrders.current = [];
    autoFilled.current = { customerName: null, email: null, phone: null };
  };

  return {
    selectedSalesOrderIds,
    setSelectedSalesOrderIds,
    resetSalesOrderSelection,
    handleRecentPaymentSelected,
    handleSalesOrderSelected,
    handleSalesOrderDeselected,
  };
};

export default usePrefillPaymentRequest;
